// IntroSequence.jsx - The EOS Reveal
// "Show them the mountain before they start climbing."

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { debug } from '../lib/debug.js';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RARE_AIRCRAFT } from '../systems/EOSSystem.js';
import { soundManager, SOUNDS } from '../systems/SoundManager.js';
import CollectibleCard, { getRarityColors } from '../components/CollectibleCard.jsx';
import LanguageSelector from '../components/LanguageSelector.jsx';
import EAWelcomeModal, { shouldShowEAWelcome } from '../components/modals/EAWelcomeModal.jsx';
import { useRadio } from '../components/ui/RadioPlayer.jsx';

// ============================================================================
// RARITY CONFIG - Matching AircraftMarket.jsx Pokemon-style reveal
// ============================================================================
const RARITY_CONFIG = {
  rare: {
    primary: '#60ffd0',
    text: '#a7f3d0',
    label: 'RARE',
    button: 'CLAIM',
    odds: '79.5%',
  },
  relic: {
    primary: '#b8860b',
    text: '#daa520',
    label: 'RELIC',
    button: 'CLAIM',
    odds: '10%',
  },
  anomalous: {
    primary: '#ef4444',
    text: '#fca5a5',
    label: 'ANOMALOUS',
    button: '...WHAT?',
    odds: '5%',
  },
  legendary: {
    primary: '#f59e0b',
    text: '#fcd34d',
    label: 'LEGENDARY',
    button: 'CLAIM',
    odds: '5%',
  },
  mythic: {
    primary: '#a855f7',
    text: '#d8b4fe',
    label: 'MYTHIC',
    button: 'CLAIM',
    odds: '0.1%',
  },
};

// ============================================================================
// AIRCRAFT EXAMPLES FOR EACH TIER
// ============================================================================
const getExampleAircraft = (rarity) => {
  const pool = RARE_AIRCRAFT[rarity];
  if (!pool || pool.length === 0) return null;

  // Pick specific interesting ones for the intro
  const picks = {
    rare: '747-9',
    relic: 'spirit-of-st-louis',
    anomalous: 'agee-whells',
    legendary: '797-9000-cumulus',
  };

  const targetId = picks[rarity];
  return pool.find(a => a.id === targetId) || pool[0];
};

// ============================================================================
// TEXT ANIMATION VARIANTS
// ============================================================================
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// ============================================================================
// SCREEN COMPONENTS
// ============================================================================

function Screen1({ onContinue }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.h1
        variants={fadeInUp}
        className="text-4xl md:text-5xl font-light mb-12 tracking-wider"
        style={{ color: '#60ffd0' }}
      >
        {t('intro.screen1.welcome')}
      </motion.h1>

      <motion.div variants={fadeInUp} className="space-y-4 text-xl text-gray-300 mb-12">
        <p>{t('intro.screen1.starting1')}</p>
        <p>{t('intro.screen1.starting2')}</p>
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-4 text-lg text-gray-400 mb-16">
        <p>{t('intro.screen1.mostPlayers')}</p>
        <p className="text-white font-semibold mt-6">{t('intro.screen1.notGoal')}</p>
      </motion.div>

      <ContinueButton onClick={onContinue} />
    </motion.div>
  );
}

function Screen2({ onContinue }) {
  const { t } = useTranslation();
  const [showEOS, setShowEOS] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowEOS(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl text-gray-300 mb-16"
      >
        {t('intro.screen2.aimingFor')}
      </motion.p>

      <AnimatePresence>
        {showEOS && (
          <motion.h1
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-4xl md:text-6xl font-bold mb-16"
            style={{
              color: '#f59e0b',
              textShadow: '0 0 40px rgba(245, 158, 11, 0.5)',
            }}
          >
            {t('intro.screen2.eos')}
          </motion.h1>
        )}
      </AnimatePresence>

      {showEOS && <ContinueButton onClick={onContinue} delay={0.5} />}
    </motion.div>
  );
}

function Screen3({ onContinue }) {
  const { t } = useTranslation();
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowReset(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const requirements = [
    t('intro.screen3.requirements.tier5'),
    t('intro.screen3.requirements.onTime'),
    t('intro.screen3.requirements.zeroCancellations'),
    t('intro.screen3.requirements.positiveCash'),
    t('intro.screen3.requirements.aircraft30'),
  ];

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-8">
        {t('intro.screen3.proof')}
      </motion.p>

      <motion.p variants={fadeInUp} className="text-lg text-gray-400 mb-8">
        {t('intro.screen3.toEarn')} <span className="text-white font-bold">{t('intro.screen3.consecutiveDays')}</span>:
      </motion.p>

      <motion.div variants={fadeInUp} className="text-left max-w-md mx-auto mb-8 space-y-2">
        {requirements.map((req, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="flex items-center gap-3"
          >
            <span style={{ color: '#22c55e' }}>&#10003;</span>
            <span className="text-gray-300">{req}</span>
          </motion.div>
        ))}
      </motion.div>

      <motion.p variants={fadeInUp} className="text-lg text-gray-400 mb-4">
        {t('intro.screen3.missAny')}
      </motion.p>

      <AnimatePresence>
        {showReset && (
          <motion.p
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-xl font-bold mb-12"
            style={{ color: '#ef4444' }}
          >
            {t('intro.screen3.streakResets')}
          </motion.p>
        )}
      </AnimatePresence>

      {showReset && <ContinueButton onClick={onContinue} />}
    </motion.div>
  );
}

function Screen4({ onContinue }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-8">
        {t('intro.screen4.unlocks')} <span style={{ color: '#f59e0b' }}>{t('intro.screen4.goldA380')}</span>.
      </motion.p>

      <motion.div
        variants={fadeInUp}
        className="mb-8"
      >
        <img
          src="./aircraft/a380.png"
          alt="Gold A380"
          className="w-48 h-auto mx-auto"
          style={{
            filter: 'drop-shadow(0 0 30px #f59e0b) sepia(1) saturate(3) hue-rotate(-10deg) brightness(1.1)',
          }}
        />
      </motion.div>

      <motion.p variants={fadeInUp} className="text-3xl font-bold mb-4" style={{ color: '#f59e0b' }}>
        {t('intro.screen4.price')}
      </motion.p>

      <motion.div variants={fadeInUp} className="text-lg text-gray-400 mb-12">
        <p>{t('intro.screen4.notBuying')}</p>
        <p className="text-white font-semibold mt-2">{t('intro.screen4.buyingRoll')}</p>
      </motion.div>

      <ContinueButton onClick={onContinue} />
    </motion.div>
  );
}

function Screen5({ onContinue }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-4">
        {t('intro.screen5.eachPurchase')}
      </motion.p>

      <motion.p variants={fadeInUp} className="text-lg text-gray-400 mb-12">
        <span className="text-white font-bold">{t('intro.screen5.aircraftCount')}</span> {t('intro.screen5.acrossTiers')} <span className="text-white font-bold">{t('intro.screen5.tierCount')}</span> {t('intro.screen5.ofRarity')}
      </motion.p>

      {/* Tier preview grid */}
      <motion.div variants={fadeInUp} className="grid grid-cols-5 gap-3 mb-12">
        {['rare', 'relic', 'anomalous', 'legendary', 'mythic'].map((tier) => {
          const config = RARITY_CONFIG[tier];
          return (
            <div key={tier} className="text-center">
              <div
                className="w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center"
                style={{
                  background: `radial-gradient(circle, ${config.primary}30 0%, transparent 70%)`,
                  border: `2px solid ${config.primary}`,
                  boxShadow: `0 0 15px ${config.primary}60`,
                }}
              >
                <span style={{ color: config.primary }}>?</span>
              </div>
              <div className="text-xs font-bold" style={{ color: config.primary }}>
                {t(`intro.rarity.${tier}`)}
              </div>
              <div className="text-xs text-gray-500">{config.odds}</div>
            </div>
          );
        })}
      </motion.div>

      <ContinueButton onClick={onContinue} />
    </motion.div>
  );
}

function Screen6({ onContinue }) {
  const { t } = useTranslation();
  const aircraft = getExampleAircraft('rare');

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <span className="text-sm font-bold tracking-wider" style={{ color: RARITY_CONFIG.rare.primary }}>
          {t('intro.screen6.rareChance', { odds: RARITY_CONFIG.rare.odds })}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <CollectibleCard aircraft={aircraft} rarity="rare" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-gray-400"
      >
        <p>{t('intro.screen6.description1')}</p>
        <p className="text-gray-500 mt-2">{t('intro.screen6.description2')}</p>
      </motion.div>

      <ContinueButton onClick={onContinue} label={t('intro.nextTier')} delay={0.8} />
    </motion.div>
  );
}

function Screen7({ onContinue }) {
  const { t } = useTranslation();
  const aircraft = getExampleAircraft('relic');

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <span className="text-sm font-bold tracking-wider" style={{ color: RARITY_CONFIG.relic.primary }}>
          {t('intro.screen7.relicChance', { odds: RARITY_CONFIG.relic.odds })}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <CollectibleCard aircraft={aircraft} rarity="relic" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-gray-400"
      >
        <p>{t('intro.screen7.description1')}</p>
        <p className="mt-2">{t('intro.screen7.description2')}</p>
        <p className="text-white mt-4">{t('intro.screen7.description3')}</p>
      </motion.div>

      <ContinueButton onClick={onContinue} label={t('intro.nextTier')} delay={0.8} />
    </motion.div>
  );
}

function Screen8({ onContinue }) {
  const { t } = useTranslation();
  const aircraft = getExampleAircraft('anomalous');
  const [glitch, setGlitch] = useState(false);

  // Occasional glitch effect
  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 100);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      className={`text-center max-w-2xl mx-auto px-6 ${glitch ? 'translate-x-1' : ''}`}
      style={{ transition: 'transform 0.05s' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <span
          className="text-sm font-bold tracking-wider"
          style={{
            color: RARITY_CONFIG.anomalous.primary,
            textShadow: glitch ? '2px 0 #ff0000, -2px 0 #00ff00' : 'none',
          }}
        >
          {t('intro.screen8.anomalousChance', { odds: RARITY_CONFIG.anomalous.odds })}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <CollectibleCard aircraft={aircraft} rarity="anomalous" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-gray-400"
      >
        <p className="text-white">{t('intro.screen8.dontFollow')}</p>
        <div className="mt-4 space-y-1 text-sm">
          <p>{t('intro.screen8.bermudan')}</p>
          <p>{t('intro.screen8.passengerZero')}</p>
          <p>{t('intro.screen8.quantumHopper')}</p>
        </div>
        <p className="text-purple-400 mt-4 font-semibold">
          {t('intro.screen8.notJust')}
        </p>
      </motion.div>

      <ContinueButton onClick={onContinue} label={t('intro.nextTier')} delay={0.8} />
    </motion.div>
  );
}

function Screen9({ onContinue }) {
  const { t } = useTranslation();
  const aircraft = getExampleAircraft('legendary');

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <span className="text-sm font-bold tracking-wider" style={{ color: RARITY_CONFIG.legendary.primary }}>
          {t('intro.screen9.legendaryChance', { odds: RARITY_CONFIG.legendary.odds })}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <CollectibleCard aircraft={aircraft} rarity="legendary" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-gray-400"
      >
        <p>{t('intro.screen9.description1')}</p>
        <p className="mt-2">{t('intro.screen9.description2')}</p>
        <p className="text-amber-400 mt-4">{t('intro.screen9.description3')}</p>
      </motion.div>

      <ContinueButton onClick={onContinue} label={t('intro.nextTier')} delay={0.8} />
    </motion.div>
  );
}

function Screen10({ onContinue }) {
  const { t } = useTranslation();
  const [showRewards, setShowRewards] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShowRewards(true), 2000);
    const t2 = setTimeout(() => setShowSecret(true), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        // Darker, more ominous background
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <span
          className="text-sm font-bold tracking-wider"
          style={{
            color: RARITY_CONFIG.mythic.primary,
            textShadow: `0 0 20px ${RARITY_CONFIG.mythic.primary}`,
          }}
        >
          {t('intro.screen10.mythicChance', { odds: RARITY_CONFIG.mythic.odds })}
        </span>
      </motion.div>

      {/* Silhouette card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <CollectibleCard aircraft={null} rarity="mythic" isSilhouette />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-6 text-gray-400"
      >
        <p className="text-white text-lg">{t('intro.screen10.oneAircraft')}</p>
        <p className="text-gray-500">{t('intro.screen10.onePerServer')}</p>
      </motion.div>

      <AnimatePresence>
        {showRewards && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-left max-w-sm mx-auto"
          >
            <p className="text-gray-400 mb-4">{t('intro.screen10.ifYouRoll')}</p>
            <div className="space-y-2 text-sm">
              <p style={{ color: '#22c55e' }}>&#8594; {t('intro.screen10.reward1')}</p>
              <p style={{ color: '#f59e0b' }}>&#8594; {t('intro.screen10.reward2')}</p>
              <p style={{ color: '#3b82f6' }}>&#8594; {t('intro.screen10.reward3')}</p>
              <p style={{ color: '#a855f7' }}>&#8594; {t('intro.screen10.reward4')}</p>
              <p style={{ color: '#ff0066' }}>&#8594; {t('intro.screen10.reward5')}</p>
              <p className="text-white mt-4">&#8594; {t('intro.screen10.reward6')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSecret && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 text-lg font-semibold"
            style={{ color: RARITY_CONFIG.mythic.primary }}
          >
            {t('intro.screen10.notTelling')}
          </motion.p>
        )}
      </AnimatePresence>

      {showSecret && <ContinueButton onClick={onContinue} delay={0.5} />}
    </motion.div>
  );
}

function Screen11({ onBegin }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className="text-center max-w-2xl mx-auto px-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-2">
        {t('intro.screen11.so')}
      </motion.p>

      <motion.div variants={fadeInUp} className="text-lg text-gray-400 my-8 space-y-2">
        <p><span className="text-white">{t('intro.screen11.starting')}</span> {t('intro.screen11.oneHub')}</p>
        <p className="mt-4">
          <span className="text-white">{t('intro.screen11.fourteenDays')}</span> {t('intro.screen11.ofExcellence')}
        </p>
        <p>{t('intro.screen11.andThe')} <span style={{ color: '#f59e0b' }}>{t('intro.screen11.goldA380')}</span>.</p>
        <p className="mt-4">
          <span className="text-white">{t('intro.screen11.collectibles')}</span> {t('intro.screen11.waitingRolled')}
        </p>
        <p className="mt-4" style={{ color: RARITY_CONFIG.mythic.primary }}>
          {t('intro.screen11.oneMythic')}
        </p>
      </motion.div>

      <motion.button
        variants={fadeInUp}
        onClick={onBegin}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="mt-12 px-16 py-6 text-2xl font-bold tracking-widest rounded-lg"
        style={{
          background: 'linear-gradient(135deg, #60ffd0, #3b82f6)',
          color: '#000',
          boxShadow: '0 0 40px rgba(96, 255, 208, 0.4)',
        }}
      >
        {t('intro.begin')}
      </motion.button>
    </motion.div>
  );
}

// ============================================================================
// CONTINUE BUTTON COMPONENT
// ============================================================================
function ContinueButton({ onClick, label, delay = 0 }) {
  const { t } = useTranslation();
  const displayLabel = label || t('intro.continue');

  const handleClick = async () => {
    debug('🔊 [EOS DEBUG] Button clicked - attempting to play INTRO_WHOOSH');
    // Ensure SoundManager is ready before playing
    await soundManager.init();
    debug('🔊 [EOS DEBUG] SoundManager init complete, calling play()');
    soundManager.play(SOUNDS.INTRO_WHOOSH, { volume: 1.0 });
    debug('🔊 [EOS DEBUG] play() called with:', SOUNDS.INTRO_WHOOSH, { volume: 1.0 });
    onClick();
  };

  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
      onClick={handleClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="mt-8 px-8 py-3 text-sm tracking-widest border rounded"
      style={{
        borderColor: '#60ffd0',
        color: '#60ffd0',
        backgroundColor: 'transparent',
      }}
    >
      {displayLabel} &#8594;
    </motion.button>
  );
}

// ============================================================================
// MAIN INTRO SEQUENCE COMPONENT
// ============================================================================
export default function IntroSequence() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showEAWelcome, setShowEAWelcome] = useState(false);
  const ambientRef = useRef(null);
  const { stop: stopRadio } = useRadio();

  // Stop any auto-playing radio music during intro sequence
  useEffect(() => {
    stopRadio();
  }, [stopRadio]);

  // Initialize sound manager and start ambient loop
  useEffect(() => {
    soundManager.init().then(() => {
      // Start ambient cockpit sound loop
      const ambient = new Audio('./audio/sfx/intro_ambient_1.wav');
      ambient.loop = true;
      ambient.volume = 0.3;
      ambient.play().catch(() => {}); // May fail if no user interaction yet
      ambientRef.current = ambient;
    }).catch(() => {});

    // Cleanup: stop ambient on unmount
    return () => {
      if (ambientRef.current) {
        ambientRef.current.pause();
        ambientRef.current = null;
      }
    };
  }, []);

  // Check if intro was already seen
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenIntro');
    if (hasSeenIntro === 'true') {
      // Skip to mode select for returning players
      navigate('/mode-select', { replace: true });
    }
  }, [navigate]);

  // Play special sound when entering Mythic screen (screen 10, index 9)
  useEffect(() => {
    if (currentScreen === 9) {
      // Mythic screen - play ominous rumble
      debug('🔊 [EOS DEBUG] Screen 9 reached - attempting INTRO_MYTHIC_RUMBLE');
      soundManager.play(SOUNDS.INTRO_MYTHIC_RUMBLE, { volume: 0.8 });
      debug('🔊 [EOS DEBUG] INTRO_MYTHIC_RUMBLE play() called');
    }
  }, [currentScreen]);

  const handleContinue = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentScreen(prev => prev + 1);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBegin = () => {
    // Stop ambient sound
    if (ambientRef.current) {
      ambientRef.current.pause();
      ambientRef.current = null;
    }
    // Play satisfying BEGIN sound (falls back to SUCCESS if not loaded)
    debug('🔊 [EOS DEBUG] Playing final sounds: INTRO_BEGIN + SUCCESS');
    soundManager.play(SOUNDS.INTRO_BEGIN);
    debug('🔊 [EOS DEBUG] INTRO_BEGIN play() called');
    soundManager.play(SOUNDS.SUCCESS); // Backup
    debug('🔊 [EOS DEBUG] SUCCESS play() called');
    // Mark intro as seen
    localStorage.setItem('hasSeenIntro', 'true');

    // Check if EA Welcome modal should be shown
    if (shouldShowEAWelcome()) {
      setShowEAWelcome(true);
    } else {
      // Navigate to mode selection
      navigate('/mode-select');
    }
  };

  const handleEAWelcomeContinue = () => {
    setShowEAWelcome(false);
    navigate('/mode-select');
  };

  const handleSkip = () => {
    // Stop ambient sound
    if (ambientRef.current) {
      ambientRef.current.pause();
      ambientRef.current = null;
    }
    localStorage.setItem('hasSeenIntro', 'true');
    navigate('/mode-select');
  };

  const screens = [
    <Screen1 key={0} onContinue={handleContinue} />,
    <Screen2 key={1} onContinue={handleContinue} />,
    <Screen3 key={2} onContinue={handleContinue} />,
    <Screen4 key={3} onContinue={handleContinue} />,
    <Screen5 key={4} onContinue={handleContinue} />,
    <Screen6 key={5} onContinue={handleContinue} />,
    <Screen7 key={6} onContinue={handleContinue} />,
    <Screen8 key={7} onContinue={handleContinue} />,
    <Screen9 key={8} onContinue={handleContinue} />,
    <Screen10 key={9} onContinue={handleContinue} />,
    <Screen11 key={10} onBegin={handleBegin} />,
  ];

  return (
    <div
      className="fixed inset-0 bg-black overflow-hidden flex items-center justify-center"
      style={{
        background: currentScreen === 9
          ? 'radial-gradient(ellipse at 50% 50%, rgba(45, 10, 26, 0.8) 0%, rgba(0, 0, 0, 1) 70%)'
          : 'radial-gradient(ellipse at 50% 50%, rgba(20, 30, 48, 0.5) 0%, rgba(0, 0, 0, 1) 70%)',
      }}
    >
      {/* Language selector and Skip button */}
      <div className="absolute top-6 right-6 flex items-center gap-4">
        <LanguageSelector />
        <button
          onClick={handleSkip}
          className="text-xs tracking-widest text-gray-600 hover:text-gray-400 transition-colors"
        >
          {t('intro.skip')}
        </button>
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {screens.map((_, i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full transition-all duration-300"
            style={{
              backgroundColor: i === currentScreen ? '#60ffd0' : i < currentScreen ? '#334155' : '#1a1a1a',
              boxShadow: i === currentScreen ? '0 0 10px #60ffd0' : 'none',
            }}
          />
        ))}
      </div>

      {/* Screen content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentScreen}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full"
        >
          {screens[currentScreen]}
        </motion.div>
      </AnimatePresence>

      {/* EA Welcome Modal - shows once after BEGIN */}
      <EAWelcomeModal
        isOpen={showEAWelcome}
        onContinue={handleEAWelcomeContinue}
      />
    </div>
  );
}
